import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import api from "../../utils/api/axiosInstance";
import { useToast } from "../../contexts/ToastContext";
import Modal from "../common/Modal";
import { FaStar, FaRegStar, FaPaperPlane, FaCommentDots } from "react-icons/fa";
import { getAppFeedbackSourceLabel } from "../../constants/appFeedbackSources";
import styles from "../../styles/components/feedback/AppFeedbackPrompt.module.css";

const AppFeedbackPrompt = ({ source = "other", prompt, metadata }) => {
  const location = useLocation();
  const { showToast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const areaLabel = getAppFeedbackSourceLabel(source).toLowerCase();
  const defaultPrompt =
    prompt || `How is the ${areaLabel} working for you?`;

  const handleClose = () => {
    if (!submitting) {
      setModalOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating && !comment.trim()) {
      showToast({ message: "Add a rating or comment first", type: "error" });
      return;
    }

    const pagePath = `${location.pathname}${location.search || ""}`.slice(
      0,
      500,
    );

    try {
      setSubmitting(true);
      await api.post("/app-feedback", {
        source,
        pagePath,
        rating: rating || undefined,
        comment: comment.trim() || undefined,
        metadata:
          metadata && typeof metadata === "object" ? metadata : undefined,
      });
      setSubmitted(true);
      setModalOpen(false);
    } catch (err) {
      showToast({
        message: err.response?.data?.message || "Failed to send feedback",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return null;
  }

  return createPortal(
    <>
      <button
        type="button"
        className={styles.floatingButton}
        onClick={() => setModalOpen(true)}
        aria-label="Give feedback"
      >
        <FaCommentDots aria-hidden="true" />
        <span className={styles.buttonLabel}>Give feedback</span>
      </button>

      <Modal
        isOpen={modalOpen}
        onClose={handleClose}
        className={styles.modalBox}
        ariaLabelledBy="app-feedback-modal-title"
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          <h2 id="app-feedback-modal-title" className={styles.prompt}>
            {defaultPrompt}
          </h2>

          <div className={styles.stars} role="group" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                className={styles.starButton}
                onClick={() => setRating(value)}
                aria-label={`Rate ${value} out of 5`}
              >
                {value <= rating ? (
                  <FaStar className={styles.starFilled} />
                ) : (
                  <FaRegStar className={styles.starEmpty} />
                )}
              </button>
            ))}
          </div>

          <textarea
            className={styles.textarea}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What works well? What could be better?"
            maxLength={1000}
            rows={3}
          />

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={submitting}
            >
              <FaPaperPlane aria-hidden="true" />
              {submitting ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </form>
      </Modal>
    </>,
    document.body,
  );
};

export default AppFeedbackPrompt;
